import type { Email } from '../useData';
import { formatLocaleDateTime } from '../../../config/appConfig';
import { classifyBodyKind, htmlHasRichContent, htmlHasSlateRichFormatting, isTrivialHtml } from './classifyBodyKind';
import { plainBodyFromParts } from './htmlToPlainText';
import type { ComposeBodyDocument, ComposeBodyKind } from './types';

function formatAddr(a: { name?: string; email: string }): string {
  const name = a?.name?.trim();
  const email = a?.email || '';
  return name ? `${name} <${email}>` : email;
}

function formatDateTime(dateStr: string): string {
  try {
    return formatLocaleDateTime(dateStr);
  } catch {
    return dateStr;
  }
}

export function buildReplyForwardHeaderLines(email: Email): string[] {
  const fromList = Array.isArray(email.from) ? email.from : email.from ? [email.from] : [];
  const toList = Array.isArray(email.to) ? email.to : email.to ? [email.to] : [];
  const ccList = Array.isArray(email.cc) ? email.cc : email.cc ? [email.cc] : [];
  const fromStr = fromList.map(formatAddr).filter(Boolean).join(', ');
  const toStr = toList.map(formatAddr).filter(Boolean).join(', ');
  const ccStr = ccList.map(formatAddr).filter(Boolean).join(', ');
  const dateVal = email.date ? formatDateTime(email.date) : '';
  const lines = [`From: ${fromStr}`, `Sent: ${dateVal}`, `To: ${toStr}`];
  if (ccStr) lines.push(`Cc: ${ccStr}`);
  lines.push(`Subject: ${email.subject ?? ''}`);
  return lines;
}

function quotedBodyKind(email: Email): ComposeBodyKind {
  const html = email.bodyHtml?.trim() ?? '';
  if (html && !isTrivialHtml(html)) {
    if (htmlHasSlateRichFormatting(html) || htmlHasRichContent(html)) return 'rich';
  }
  return classifyBodyKind(email.bodyHtml, email.bodyText);
}

export function emailToComposeDoc(
  email: Email,
  mode: 'reply' | 'forward',
  options?: { userBody?: string; signature?: string },
): ComposeBodyDocument {
  const kind = quotedBodyKind(email);
  const heading = mode === 'reply' ? 'Original message' : 'Forwarded message';
  const quotedPlain =
    kind === 'plain' ? plainBodyFromParts(email.bodyHtml, email.bodyText) : undefined;

  return {
    bodyKind: kind,
    userBody: options?.userBody ?? '',
    signature: options?.signature,
    quoted: {
      mode,
      headerLines: [heading, ...buildReplyForwardHeaderLines(email)],
      bodyText: quotedPlain,
      bodyHtml: email.bodyHtml ?? undefined,
      bodyKind: kind,
    },
  };
}
